import { useState } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Card, CardContent } from "@/components/ui/card";
import { HardHat, AlertCircle } from "lucide-react";
import { toast } from "sonner";

export default function Login() {
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);
  const { login } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();

  const onSubmit = async (e) => {
    e.preventDefault();
    setError(""); setLoading(true);
    try {
      await login(email, password);
      const to = location.state?.from?.pathname || "/";
      navigate(to, { replace: true });
      toast.success("Accesso effettuato");
    } catch (e) {
      setError(e.response?.data?.detail || "Errore di accesso");
    } finally { setLoading(false); }
  };

  return (
    <div className="min-h-screen grid lg:grid-cols-2 bg-white">
      <div className="relative hidden lg:flex flex-col justify-between items-start p-12 bg-white">
        <img
          src="/brand/inviluppo-logo.png"
          alt="INVILUPPO"
          className="h-32 xl:h-36 w-auto object-contain select-none"
          draggable={false}
        />
        <div className="max-w-xl">
          <p className="text-3xl xl:text-4xl font-semibold leading-snug tracking-tight text-slate-900">
            Ogni grande opera nasce da un progetto.
          </p>
          <p className="text-3xl xl:text-4xl font-semibold leading-snug tracking-tight text-amber-600 mt-2">
            Ogni buon risultato nasce dal controllo.
          </p>
          <div className="mt-10 pt-6 border-t border-slate-200">
            <div className="flex items-center gap-3 mb-3">
              <div className="w-10 h-10 bg-amber-500 rounded-lg flex items-center justify-center">
                <HardHat className="h-5 w-5 text-slate-950" />
              </div>
              <h1 className="text-2xl font-extrabold tracking-tight text-slate-900">Gestione Cantieri</h1>
            </div>
            <p className="text-slate-600 max-w-md leading-relaxed text-sm">
              ERP operativo professionale — cantieri, computi, materiali, manodopera, ticket e calendario, in un'unica piattaforma.
            </p>
          </div>
        </div>
        <span />
      </div>
      <div className="flex items-center justify-center p-6 lg:p-12 bg-slate-950">
        <Card className="w-full max-w-sm border border-slate-800/70 bg-slate-900 text-slate-100 shadow-2xl rounded-2xl">
          <CardContent className="p-10">
            <div className="lg:hidden flex flex-col items-start gap-3 mb-8">
              <div className="inline-flex items-center justify-center rounded-xl bg-white p-2 shadow-md">
                <img src="/brand/inviluppo-logo.png" alt="INVILUPPO" className="h-14 w-auto object-contain select-none" draggable={false} />
              </div>
              <div>
                <p className="text-base font-semibold text-white leading-snug">Ogni grande opera nasce da un progetto.</p>
                <p className="text-base font-semibold text-amber-500 leading-snug">Ogni buon risultato nasce dal controllo.</p>
              </div>
              <div className="flex items-center gap-2 pt-1">
                <div className="w-8 h-8 bg-amber-500 rounded-lg flex items-center justify-center">
                  <HardHat className="h-4 w-4 text-slate-950" />
                </div>
                <h1 className="text-lg font-bold">Gestione Cantieri</h1>
              </div>
            </div>
            <h2 className="text-3xl font-semibold tracking-tight mb-10">Accedi</h2>

            <form onSubmit={onSubmit} className="space-y-6">
              <div className="space-y-2">
                <Label htmlFor="email" className="text-slate-400 text-[11px] uppercase tracking-[0.18em] font-medium">Email</Label>
                <Input
                  id="email" type="email" required autoFocus value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  data-testid="login-email-input"
                  className="h-11 bg-transparent border-0 border-b border-slate-700 rounded-none px-0 text-slate-100 placeholder:text-slate-600 focus-visible:ring-0 focus-visible:border-amber-500 transition-colors"
                  placeholder="tu@dominio.it"
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="password" className="text-slate-400 text-[11px] uppercase tracking-[0.18em] font-medium">Password</Label>
                <Input
                  id="password" type="password" required value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  data-testid="login-password-input"
                  className="h-11 bg-transparent border-0 border-b border-slate-700 rounded-none px-0 text-slate-100 placeholder:text-slate-600 focus-visible:ring-0 focus-visible:border-amber-500 transition-colors"
                  placeholder="••••••••"
                />
              </div>
              {error && (
                <div data-testid="login-error" className="flex items-start gap-2 text-sm bg-rose-950/50 border border-rose-800/60 text-rose-200 rounded-lg px-3 py-2">
                  <AlertCircle className="h-4 w-4 mt-0.5 shrink-0" /> {error}
                </div>
              )}
              <Button type="submit" disabled={loading} data-testid="login-submit-button"
                className="w-full h-11 bg-amber-500 hover:bg-amber-600 text-slate-950 font-semibold rounded-lg mt-2 transition-colors">
                {loading ? "Accesso…" : "Accedi"}
              </Button>
            </form>
          </CardContent>
        </Card>
      </div>
    </div>
  );
}
